import { BookingLayout, GlobalLayout, MainLayout } from 'app/layouts';
import { getInfoFromToken, isAuthKeyEmpty, UserRoles } from 'entities/User';
import { AdminPanelPage } from 'pages/AdminPanelPage';
import { BookingPage } from 'pages/BookingPage';
import { BoxesPage } from 'pages/BoxesPage';
import { CheckoutPage } from 'pages/CheckoutPage';
import { CitiesPage } from 'pages/CitiesPage';
import { ContractsPage } from 'pages/ContractsPage';
import { ForgotPasswordPage } from 'pages/ForgotPasswordPage';
import { InvoicesPage } from 'pages/InvoicesPage';
import { LoginPage } from 'pages/LoginPage';
import { MyBoxesPage } from 'pages/MyBoxesPage';
import { NotFoundPage } from 'pages/NotFoundPage';
import { PaymentFailedPage } from 'pages/PaymentFailedPage';
import { PaymentSuccessPage } from 'pages/PaymentSuccessPage';
import { ReservationsPage } from 'pages/ReservationsPage';
import { ResetPasswordPage } from 'pages/ResetPasswordPage';
import { UserContractsPage } from 'pages/UserContractsPage';
import { UsersPage } from 'pages/UsersPage';
import { WarehousesPage } from 'pages/WarehousesPage';
import { createBrowserRouter, LoaderFunction, LoaderFunctionArgs, redirect, redirectDocument, RouteObject } from 'react-router-dom';
import { ProfilePage } from 'pages/ModernProfilePage';
import { WarehouseConstructorPage } from 'pages/WarehouseConstructorPage';
import { WarehousePage } from 'pages/WarehousePage';
import { WarehouseHistoryPage } from 'pages/WarehouseHistoryPage';
import { REDIRECTED_FROM_URL_KEY } from './constants';
import { AppRoutes } from './types';
import { NotesPage } from 'pages/NotesPage';
import { LateChargeSettingsPage } from 'pages/LateChargeSettingsPage';
import { CreditNotesPage } from 'pages/CreditNotesPage';
import { InvoicesInsurancePage } from 'pages/InvoicesInsurancePage';
import { InsuranceSettingsPage } from 'pages/InsuranceSettingsPage';
import { DepositsPage } from 'pages/DepositsPage';
import { NoResultPage } from 'pages/NoResultPage';
import { BoxesOccupancyPage } from 'pages/BoxesOccupancyPage';
import { IncomeStatisticsPage } from 'pages/IncomeStatisticsPage';
import { LoginProcessPage } from 'pages/LoginProcessPage';
import { DiscountsPage } from 'pages/DiscountsPage';
import { MyBookingsPage } from 'pages/MyBookingsPage';
import { MobileMyAccountPage } from 'pages/MobileMyAccountPage';
import { MobileMyLatestActivityPage } from 'pages/MobileMyLatestActivityPage';
import { MobileMyInvoicesPage } from 'pages/MobileMyInvoicesPage';
import { CustomerLayout } from 'app/layouts/ui/CustomerLayout';

const protectedRouteLoader =
  (allowedRoles: UserRoles[]) =>
  ({ request }: LoaderFunctionArgs): Nullable<Response> => {
    const isAuthorized = !isAuthKeyEmpty();
    const userRole = getInfoFromToken()?.role;

    const hasAccess = userRole ? allowedRoles.includes(userRole) : false;

    if (!isAuthorized || !userRole || !hasAccess) {
      sessionStorage.setItem(REDIRECTED_FROM_URL_KEY, request.url);

      return redirect(AppRoutes.LOGIN);
    }
    sessionStorage.removeItem(REDIRECTED_FROM_URL_KEY);

    return null;
  };

export const DynamicLayout: React.FC = (): JSX.Element => {
  const role = getInfoFromToken()?.role;

  if (role === UserRoles.CUSTOMER) {
    return <CustomerLayout />;
  }

  return <MainLayout />;
};

const rootRouteLoader = (): Response => {
  const userRole = getInfoFromToken()?.role;

  const redirectedFromURL = sessionStorage.getItem(REDIRECTED_FROM_URL_KEY);

  const redirectionMap: Record<UserRoles, AppRoutes> = {
    [UserRoles.SUPERUSER]: AppRoutes.WAREHOUSES,
    [UserRoles.MANAGER]: AppRoutes.WAREHOUSES,
    [UserRoles.CUSTOMER]: AppRoutes.MY_BOXES,
    [UserRoles.STAFF]: AppRoutes.BOXES,
    [UserRoles.PARTNER]: AppRoutes.WAREHOUSES,
  };

  return redirectedFromURL ? redirectDocument(redirectedFromURL) : redirect(userRole ? redirectionMap[userRole] : AppRoutes.BOOKING);
};

const checkAssignedWarehousesLoader = (): Nullable<Response> => {
  const isSuperuserRole = getInfoFromToken()?.role === UserRoles.SUPERUSER;

  const hasAssignedWarehouses = Boolean(getInfoFromToken()?.assignedWarehouseIds?.length);

  const showPage = isSuperuserRole || hasAssignedWarehouses;

  return showPage ? null : redirect(AppRoutes.NO_RESULT);
};

const loginPageLoader = (): Nullable<Response> => {
  const isAuthorized = !isAuthKeyEmpty();

  if (isAuthorized) {
    return redirect(AppRoutes.HOME);
  }

  return null;
};

const combineLoaders =
  (...loaders: LoaderFunction[]) =>
  async (args: CustomAny) => {
    let result = null;

    for (const loader of loaders) {
      result = await loader(args);
    }

    return result;
  };

const bookingRoutes: RouteObject[] = [
  {
    index: true,
    Component: BookingPage,
  },
];

const mainRoutes: RouteObject[] = [
  {
    path: AppRoutes.HOME,
    Component: DynamicLayout,
    children: [
      {
        index: true,
        loader: rootRouteLoader,
      },
      {
        path: AppRoutes.WAREHOUSES,
        Component: WarehousesPage,
        loader: combineLoaders(
          protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER, UserRoles.STAFF, UserRoles.PARTNER]),
          checkAssignedWarehousesLoader,
        ),
      },
      {
        path: `${AppRoutes.WAREHOUSES}/:warehouseId`,
        Component: WarehousePage,
        loader: protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER]),
      },
      {
        path: `${AppRoutes.WAREHOUSES}/:warehouseId/history`,
        Component: WarehouseHistoryPage,
        loader: protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER, UserRoles.PARTNER]),
      },
      {
        path: `${AppRoutes.WAREHOUSES}/:warehouseId/constructor`,
        Component: WarehouseConstructorPage,
        loader: protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER]),
      },
      {
        path: AppRoutes.CITIES,
        Component: CitiesPage,
        loader: protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER]),
      },
      {
        path: AppRoutes.USERS,
        Component: UsersPage,
        loader: protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER]),
      },
      {
        path: `${AppRoutes.USERS}/:userId/contracts`,
        Component: UserContractsPage,
        loader: combineLoaders(
          protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER, UserRoles.PARTNER]),
          checkAssignedWarehousesLoader,
        ),
      },
      {
        path: AppRoutes.CONTRACTS,
        Component: ContractsPage,
        loader: combineLoaders(
          protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER, UserRoles.PARTNER]),
          checkAssignedWarehousesLoader,
        ),
      },
      {
        path: AppRoutes.RESERVATIONS,
        Component: ReservationsPage,
        loader: combineLoaders(
          protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER, UserRoles.PARTNER]),
          checkAssignedWarehousesLoader,
        ),
      },
      {
        path: AppRoutes.STATISTICS,
        Component: null,
        loader: protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER, UserRoles.PARTNER]),
      },
      {
        path: AppRoutes.BOXES_OCCUPANCY,
        Component: BoxesOccupancyPage,
        loader: protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER, UserRoles.PARTNER]),
      },
      {
        path: AppRoutes.INCOME_STATISTICS,
        Component: IncomeStatisticsPage,
        loader: protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER, UserRoles.PARTNER]),
      },
      {
        path: AppRoutes.INVOICES,
        Component: InvoicesPage,
        loader: combineLoaders(
          protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER, UserRoles.PARTNER]),
          checkAssignedWarehousesLoader,
        ),
      },
      {
        path: AppRoutes.CREDIT_NOTES,
        Component: CreditNotesPage,
        loader: combineLoaders(
          protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER, UserRoles.PARTNER]),
          checkAssignedWarehousesLoader,
        ),
      },
      {
        path: AppRoutes.INSURANCE,
        Component: InvoicesInsurancePage,
        loader: combineLoaders(
          protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER, UserRoles.PARTNER]),
          checkAssignedWarehousesLoader,
        ),
      },
      {
        path: AppRoutes.DEPOSITS,
        Component: DepositsPage,
        loader: combineLoaders(
          protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER, UserRoles.PARTNER]),
          checkAssignedWarehousesLoader,
        ),
      },
      {
        path: AppRoutes.BOXES,
        Component: BoxesPage,
        loader: combineLoaders(
          protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER, UserRoles.STAFF, UserRoles.PARTNER]),
          checkAssignedWarehousesLoader,
        ),
      },
      {
        path: AppRoutes.NOTES,
        Component: NotesPage,
        loader: protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER]),
      },
      {
        path: AppRoutes.TASKS,
        Component: null,
        loader: protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER]),
      },
      {
        path: AppRoutes.GENERAL,
        Component: null,
        loader: protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER]),
      },
      {
        path: AppRoutes.BILLING_DOCUMENTS,
        Component: null,
        loader: combineLoaders(
          protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER, UserRoles.PARTNER]),
          checkAssignedWarehousesLoader,
        ),
      },
      {
        path: AppRoutes.INSURANCE_SETTINGS,
        Component: InsuranceSettingsPage,
        loader: protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER]),
      },
      {
        path: AppRoutes.LATE_CHARGE_SETTINGS,
        Component: LateChargeSettingsPage,
        loader: protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER]),
      },
      {
        path: AppRoutes.MY_BOXES,
        Component: MyBoxesPage,
        loader: protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER, UserRoles.CUSTOMER, UserRoles.STAFF, UserRoles.PARTNER]),
      },
      {
        path: `${AppRoutes.MY_BOXES}/:warehouseId`,
        Component: MyBookingsPage,
        loader: protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER, UserRoles.CUSTOMER, UserRoles.STAFF, UserRoles.PARTNER]),
      },
      {
        path: AppRoutes.GLOBAL_SETTINGS,
        Component: null,
        loader: protectedRouteLoader([UserRoles.SUPERUSER]),
      },
      {
        path: AppRoutes.ADMIN_PANEL,
        Component: AdminPanelPage,
        loader: protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER]),
      },
      {
        path: `${AppRoutes.PROFILE}/:userId`,
        Component: ProfilePage,
        loader: protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER, UserRoles.CUSTOMER, UserRoles.STAFF, UserRoles.PARTNER]),
      },
      {
        path: `${AppRoutes.PROFILE}/:userId/account`,
        Component: MobileMyAccountPage,
        loader: protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER, UserRoles.CUSTOMER, UserRoles.STAFF, UserRoles.PARTNER]),
      },
      {
        path: `${AppRoutes.PROFILE}/:userId/latest-activity`,
        Component: MobileMyLatestActivityPage,
        loader: protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER, UserRoles.CUSTOMER, UserRoles.STAFF, UserRoles.PARTNER]),
      },
      {
        path: `${AppRoutes.PROFILE}/:userId/invoices`,
        Component: MobileMyInvoicesPage,
        loader: protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER, UserRoles.CUSTOMER, UserRoles.STAFF, UserRoles.PARTNER]),
      },
      {
        path: AppRoutes.NO_RESULT,
        Component: NoResultPage,
      },
      {
        path: AppRoutes.DISCOUNTS,
        Component: DiscountsPage,
        loader: combineLoaders(
          protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER, UserRoles.PARTNER]),
          checkAssignedWarehousesLoader,
        ),
      },
    ],
  },
  {
    path: AppRoutes.BOOKING,
    Component: BookingLayout,
    children: bookingRoutes,
  },
  {
    path: AppRoutes.LOGIN,
    Component: LoginPage,
    loader: loginPageLoader,
  },
  {
    path: AppRoutes.FORGOT_PASSWORD,
    Component: ForgotPasswordPage,
    loader: loginPageLoader,
  },
  {
    path: `${AppRoutes.RESET_PASSWORD}/:resetPasswordId`,
    Component: ResetPasswordPage,
    loader: loginPageLoader,
  },
  {
    path: AppRoutes.NOT_FOUND,
    Component: NotFoundPage,
  },
  {
    path: AppRoutes.LOGIN_PROCESS_PAGE,
    Component: LoginProcessPage,
  },
  {
    path: AppRoutes.CHECKOUT,
    Component: CheckoutPage,
    loader: protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER, UserRoles.CUSTOMER, UserRoles.PARTNER]),
  },
  {
    path: AppRoutes.PAYMENT_SUCCESS,
    Component: PaymentSuccessPage,
    loader: protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER, UserRoles.CUSTOMER, UserRoles.PARTNER]),
  },
  {
    path: AppRoutes.PAYMENT_FAILED,
    Component: PaymentFailedPage,
    loader: protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER, UserRoles.CUSTOMER]),
  },
];

export const appRouter = createBrowserRouter([
  {
    Component: GlobalLayout,
    children: mainRoutes,
  },
]);
